'use client';

// Use only in /app folder, for next app router.

import { DEPLOYMENT_DOMAIN, DeploymentEnvironment, NEXT_PUBLIC_ZARAZ_DOMAIN } from '@env';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect } from 'react';
import { AnalyticsPagePropertiesInput, AnalyticsPlatforms } from '.cache/__types__';

import { analytics } from '@modules/analytics';
import { useCookieConsentListener } from '@hooks/use-cookie-consent-listener';

const platforms = ['android', 'api', 'card_worker', 'dash', 'firebase_functions', 'ios', 'webflow'];
function isPlatform(candidate: unknown): candidate is AnalyticsPlatforms {
  return typeof candidate === 'string' && platforms.includes(candidate);
}

export const AnalyticsProvider = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const isNonProduction = [DeploymentEnvironment.Preview, DeploymentEnvironment.NonProduction].includes(
    DEPLOYMENT_DOMAIN
  );
  const isProduction = [DeploymentEnvironment.Production].includes(DEPLOYMENT_DOMAIN);

  useCookieConsentListener();

  useEffect(() => {
    const properties: AnalyticsPagePropertiesInput = {
      path: pathname,
    };

    searchParams?.forEach((value, key) => {
      switch (key) {
        case 'utm_medium':
          properties[key] = value;
          break;
        case 'utm_source':
          properties[key] = value;
          break;
        case 'utm_campaign':
          properties[key] = value;
          break;
        case 'utm_content':
          properties[key] = value;
          break;
        case 'utm_term':
          properties[key] = value;
          break;
        case 'ref_original':
          properties[key] = value;
          break;
        case 'gclid':
          properties[key] = value;
          if (value) {
            localStorage.setItem('gclid', value); // save for when user is created
          }
          break;
        case 'fbclid':
          properties[key] = value;
          if (value) {
            localStorage.setItem('fbclid', value); // save for when user is created
          }
          break;
        case 'ref_page':
          properties[key] = value;
          break;
        case 'ref_path':
          properties[key] = value;
          break;
        case 'ref_cta':
          properties[key] = value;
          break;
        case 'ref_platform':
          if (isPlatform(value)) {
            properties[key] = value;
          }
          break;
      }
    });

    analytics.page({ properties });
  }, [pathname, searchParams]);

  return (
    <>
      {isNonProduction && (
        <script
          id="cookieyes"
          type="text/javascript"
          src="https://cdn-cookieyes.com/client_data/ae1c68dbee2c638eab1b2fa9/script.js"
        ></script>
      )}
      {isProduction && (
        <script
          id="cookieyes"
          type="text/javascript"
          src="https://cdn-cookieyes.com/client_data/eb0ffba2d3ff97c82eb21dee/script.js"
        ></script>
      )}
      {NEXT_PUBLIC_ZARAZ_DOMAIN && (
        // Before interactive as Zaraz loads other tracking scripts
        <Script strategy="beforeInteractive" src={`https://${NEXT_PUBLIC_ZARAZ_DOMAIN}/cdn-cgi/zaraz/i.js`} />
      )}
      <Script strategy="afterInteractive" src="/partnerstack.js" />
    </>
  );
};
